import {
  DxDiagnosisResult,
  DxDiagnosisResultAdviceContent,
} from '@src/api/types/shared/dxDiagnosis.ts';
import { DxDiagnosisResultMaturityEnum } from '@src/enum/dxDiagnosisResultMaturityEnum.ts';
import { DxDiagnosisStepCodeEnum } from '@src/enum/dxDiagnosisStepCodeEnum.ts';

import { ApiResult } from '../../apiClient/apiResult';
import { axiosClient } from '../../apiClient/axiosClient';
import { Pagination } from '../../types/pagination';

type DxDiagnosisResultIndexRequest = {
  keyword?: string;
  page?: number;
  per_page?: number;
  sort?: string;
  direction?: string;
  status?: number[];
};

type DxDiagnosisResultIndexResponse = {
  data: DxDiagnosisResult[];
  pagination: Pagination;
};

export type DxDiagnosisResultStep = {
  code: DxDiagnosisStepCodeEnum;
  name: string;
  score: number;
  questions: {
    question_no: number;
    question_text: string;
    max_score: number;
    answer: {
      chosen_text: string;
      score: number;
    };
  }[];
};

type DxDiagnosisResultAdvice = {
  steps: {
    id: number;
    name: string;
    code: string;
  }[];
  content: DxDiagnosisResultAdviceContent;
  support_services: {
    id: number;
    title: string;
  }[];
};

export type DxDiagnosisResultShowResponse = {
  diagnosis_on: string;
  company: {
    id: number;
    name: string;
    industry_text: string;
  };
  user: {
    id: number;
    family_name: string;
    first_name: string;
  };
  version: string;
  total_score: number;
  is_finished: boolean;
  stage: {
    code: DxDiagnosisResultMaturityEnum;
    name: string;
  };
  steps: DxDiagnosisResultStep[];
  advice: DxDiagnosisResultAdvice;
};

type DxDiagnosisResultScoreIndexRequest = {
  company_id: number;
};

type DxDiagnosisResultScore = {
  total_score: number;
  diagnosis_on: string;
};

type DxDiagnosisResultScoreUser = {
  id: number;
  family_name: string;
  first_name: string;
};

export type DxDiagnosisResultScoreIndexResponse = {
  company_user: DxDiagnosisResultScoreUser;
  scores: DxDiagnosisResultScore[];
}[];

type DxDiagnosisResultCreateResponse = {
  dx_diagnosis_result_id: number;
};

type DxDiagnosisResultUpdateResponse = {
  status: number;
};

export type DxDiagnosisResultCsvExportRequest = {
  status?: number[];
  sort?: string;
  direction?: string;
};

export const dxDiagnosisResultApiClient = {
  index: async (
    params: DxDiagnosisResultIndexRequest = {},
  ): Promise<ApiResult<DxDiagnosisResultIndexResponse>> => {
    return await axiosClient.get('/company/dx-diagnosis-results', { params });
  },
  show: async (id: number): Promise<ApiResult<DxDiagnosisResultShowResponse>> => {
    return await axiosClient.get(`/company/dx-diagnosis-results/${id}`);
  },
  scoreIndex: async (
    params: DxDiagnosisResultScoreIndexRequest,
  ): Promise<ApiResult<DxDiagnosisResultScoreIndexResponse>> => {
    return await axiosClient.get('/company/dx-diagnosis-results/score', { params });
  },
  create: async (): Promise<ApiResult<DxDiagnosisResultCreateResponse>> => {
    return await axiosClient.post('/company/dx-diagnosis-results', {});
  },
  update: async (
    id: number,
    data: { status: number },
  ): Promise<ApiResult<DxDiagnosisResultUpdateResponse>> => {
    return await axiosClient.put(`/company/dx-diagnosis-results/${id}`, data);
  },
  delete: async (id: number): Promise<ApiResult<void>> => {
    return await axiosClient.delete(`/company/dx-diagnosis-results/${id}`);
  },
  exportCsv: async (
    params: DxDiagnosisResultCsvExportRequest = {},
  ): Promise<ApiResult<Blob, never>> => {
    return await axiosClient.get('/company/dx-diagnosis-results/csv-export', {
      params,
      responseType: 'blob',
    });
  },
};
